import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAdmin } from '@/contexts/AdminContext';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { LayoutDashboard, Users, ShieldCheck, LogOut, Shield, Megaphone, Settings, Sun, Moon, GraduationCap, ClipboardList, ThumbsUp, Sparkles, UserX, Lock, BarChart3, Coins, Menu, X, Link2, Smartphone, RefreshCw, Download, Share, Plus, CheckCircle2, Handshake, Star, BookOpen, Store, Bell, Flag, ScrollText, ToggleRight, Database, LifeBuoy, Languages, TrendingUp } from 'lucide-react';

const navItems = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/admin/analytics', icon: TrendingUp, label: 'Behavior Analytics', perm: 'can_view_dashboard' },
  { to: '/admin/users', icon: Users, label: 'Users', perm: 'can_manage_users' },
  { to: '/admin/community-reports', icon: Flag, label: 'Reports', perm: 'can_manage_users' },
  { to: '/admin/affiliates', icon: Handshake, label: 'Affiliates', perm: 'can_manage_users' },
  { to: '/admin/locked-users', icon: Lock, label: 'Locked', perm: 'can_manage_users' },
  { to: '/admin/digests', icon: BarChart3, label: 'Digests' },
  { to: '/admin/token-usage', icon: Coins, label: 'AI Tokens', perm: 'can_view_dashboard' },
  { to: '/admin/plan-changes', icon: RefreshCw, label: 'Plan Changes', perm: 'can_view_dashboard' },
  { to: '/admin/deep-links', icon: Link2, label: 'Deep Links', perm: 'can_view_dashboard' },
  { to: '/admin/mobile-revenue', icon: Smartphone, label: 'Mobile Revenue', perm: 'can_view_dashboard' },
  { to: '/admin/store-billing', icon: Store, label: 'Store & Billing', perm: 'can_edit_settings' },
  { to: '/admin/legal', icon: ScrollText, label: 'Legal Docs', perm: 'can_edit_settings' },
  { to: '/admin/i18n', icon: Languages, label: 'Translations', perm: 'can_edit_settings' },
  { to: '/admin/notification-log', icon: Bell, label: 'Notifications', perm: 'can_view_dashboard' },
  { to: '/admin/feature-flags', icon: ToggleRight, label: 'Feature Flags', perm: 'can_edit_settings' },
  { to: '/admin/database', icon: Database, label: 'Database Health', perm: 'can_manage_admins' },
  { to: '/admin/churned-users', icon: UserX, label: 'Churned', perm: 'can_manage_users' },
  { to: '/admin/broadcasts', icon: Megaphone, label: 'Broadcasts', perm: 'can_manage_users' },
  { to: '/admin/tutorials', icon: GraduationCap, label: 'Tutorials', perm: 'can_manage_users' },
  { to: '/admin/feedback', icon: ThumbsUp, label: 'Feedback', perm: 'can_view_dashboard' },
  { to: '/admin/reviews', icon: Star, label: 'Reviews', perm: 'can_view_dashboard' },
  { to: '/admin/reflections', icon: BookOpen, label: 'Reflections', perm: 'can_view_dashboard' },
  { to: '/admin/did-you-know', icon: Sparkles, label: 'Did You Know?', perm: 'can_view_dashboard' },
  { to: '/admin/changelog', icon: Sparkles, label: "What's New", perm: 'can_manage_users' },
  { to: '/admin/faq', icon: LifeBuoy, label: 'FAQ Content', perm: 'can_manage_users' },
  { to: '/admin/audit-log', icon: ClipboardList, label: 'Audit Log', perm: 'can_view_dashboard' },
  { to: '/admin/admins', icon: ShieldCheck, label: 'Admins', perm: 'can_manage_admins' },
  { to: '/admin/settings', icon: Settings, label: 'Settings' },
];

export default function AdminLayout({ children }) {
  const { admin, logout, hasPermission } = useAdmin();
  const { isDark, toggleTheme, colors } = useAdminTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(() => localStorage.getItem('eili_admin_pwa_installed') === 'true');
  const [showIOSSteps, setShowIOSSteps] = useState(false);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Capture PWA install prompt
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    const onInstalled = () => { setIsInstalled(true); localStorage.setItem('eili_admin_pwa_installed', 'true'); };
    window.addEventListener('appinstalled', onInstalled);
    return () => { window.removeEventListener('beforeinstallprompt', handler); window.removeEventListener('appinstalled', onInstalled); };
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') { setIsInstalled(true); localStorage.setItem('eili_admin_pwa_installed', 'true'); }
    setInstallPrompt(null);
  };

  // Swap PWA manifest to admin version
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const original = link?.getAttribute('href');
    if (link) link.setAttribute('href', '/admin-manifest.json');
    return () => { if (link && original) link.setAttribute('href', original); };
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const roleColors = {
    super_admin: isDark ? 'text-amber-400 bg-amber-400/10' : 'text-amber-600 bg-amber-100',
    admin: isDark ? 'text-indigo-400 bg-indigo-400/10' : 'text-indigo-600 bg-indigo-100',
    viewer: isDark ? 'text-slate-400 bg-slate-400/10' : 'text-slate-600 bg-slate-200',
  };
  const roleClass = roleColors[admin?.role] || roleColors.viewer;

  const sidebarContent = (
    <>
      {/* Brand */}
      <div className="px-5 py-5" style={{ borderBottom: `1px solid ${colors.sidebarBorder}` }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Shield className="h-5 w-5" style={{ color: colors.accent }} />
            <span className="font-bold text-sm" style={{ color: colors.text }}>Eili Admin</span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={toggleTheme}
              className="p-1.5 rounded-lg transition-colors"
              style={{ color: colors.textMuted, background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }}
              data-testid="theme-toggle-btn"
              title={isDark ? 'Light mode' : 'Dark mode'}>
              {isDark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
            </button>
            <button onClick={() => setSidebarOpen(false)}
              className="p-1.5 rounded-lg transition-colors lg:hidden"
              style={{ color: colors.textMuted }} data-testid="close-sidebar-btn">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          if (item.perm && !hasPermission(item.perm)) return null;
          return (
            <NavLink key={item.to} to={item.to} end={item.end}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors"
              style={({ isActive }) => ({
                background: isActive ? (isDark ? 'rgba(99,102,241,0.15)' : 'rgba(79,70,229,0.1)') : 'transparent',
                color: isActive ? colors.accent : colors.textSecondary,
              })}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}>
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          );
        })}
      </nav>

      {/* User info */}
      <div className="px-4 py-4" style={{ borderTop: `1px solid ${colors.sidebarBorder}` }}>
        <div className="mb-3">
          <p className="text-sm font-medium truncate" style={{ color: colors.text }}>{admin?.name}</p>
          <p className="text-[11px] truncate" style={{ color: colors.textMuted }}>{admin?.email}</p>
          <span className={`inline-block mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${roleClass}`}>
            {admin?.role?.replace('_', ' ')}
          </span>
        </div>
        <button onClick={handleLogout}
          className="flex items-center gap-2 text-xs hover:text-red-400 transition"
          style={{ color: colors.textMuted }} data-testid="admin-logout-btn">
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
        {/* Install Admin App */}
        {!isInstalled && (
          <div className="mt-3">
            {installPrompt ? (
              <button onClick={handleInstall}
                className="flex items-center gap-2 text-xs px-3 py-2 rounded-xl font-medium transition-all hover:brightness-110 w-full"
                style={{ background: `${colors.accent}15`, color: colors.accent }}
                data-testid="admin-install-btn">
                <Download className="h-3.5 w-3.5" /> Install Admin App
              </button>
            ) : (
              <>
                <button onClick={() => setShowIOSSteps(p => !p)}
                  className="flex items-center gap-2 text-xs px-3 py-2 rounded-xl font-medium transition-all hover:brightness-110 w-full"
                  style={{ background: `${colors.accent}15`, color: colors.accent }}
                  data-testid="admin-install-guide-btn">
                  <Download className="h-3.5 w-3.5" /> {showIOSSteps ? 'Hide Instructions' : 'Install Admin App'}
                </button>
                {showIOSSteps && (
                  <div className="mt-2 space-y-2 px-1" data-testid="admin-install-steps">
                    {isIOS ? (
                      <>
                        <div className="flex items-center gap-2 text-xs" style={{ color: colors.textMuted }}>
                          <Share className="h-3 w-3 shrink-0" style={{ color: colors.accent }} />
                          <span>Tap <strong style={{ color: colors.text }}>Share</strong> in Safari</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs" style={{ color: colors.textMuted }}>
                          <Plus className="h-3 w-3 shrink-0" style={{ color: colors.accent }} />
                          <span>Tap <strong style={{ color: colors.text }}>Add to Home Screen</strong></span>
                        </div>
                        <div className="flex items-center gap-2 text-xs" style={{ color: colors.textMuted }}>
                          <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-400" />
                          <span>Tap <strong style={{ color: colors.text }}>Add</strong> to confirm</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 text-xs" style={{ color: colors.textMuted }}>
                          <Menu className="h-3 w-3 shrink-0" style={{ color: colors.accent }} />
                          <span>Tap the <strong style={{ color: colors.text }}>browser menu</strong> (3 dots)</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs" style={{ color: colors.textMuted }}>
                          <Download className="h-3 w-3 shrink-0" style={{ color: colors.accent }} />
                          <span>Tap <strong style={{ color: colors.text }}>Install app</strong> or <strong style={{ color: colors.text }}>Add to Home Screen</strong></span>
                        </div>
                        <div className="flex items-center gap-2 text-xs" style={{ color: colors.textMuted }}>
                          <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-400" />
                          <span>Tap <strong style={{ color: colors.text }}>Install</strong> to confirm</span>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {isInstalled && (
          <div className="flex items-center gap-2 text-xs mt-3 px-3 py-2 rounded-xl"
            style={{ background: 'rgba(16,185,129,0.1)', color: 'rgb(52,211,153)' }}
            data-testid="admin-app-installed">
            <CheckCircle2 className="h-3.5 w-3.5" /> App Installed
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="page-container !flex-row" style={{ background: colors.bg }} data-admin-theme={isDark ? 'dark' : 'light'}>
      {/* Desktop Sidebar.
          Reveal breakpoint set to `lg:` (1024px) so on iPad Stage
          Manager / Split View (typically 640-900px window widths),
          the 224px-wide desktop sidebar hides and the mobile overlay
          drawer takes over cleanly. Previously used `md:` which
          squished the main admin content at intermediate iPad
          multitasking widths. */}
      <aside className="hidden lg:flex w-56 shrink-0 flex-col" style={{ background: colors.sidebar, borderRight: `1px solid ${colors.sidebarBorder}` }}
        data-testid="admin-sidebar">
        {sidebarContent}
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-sidebar-overlay">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 flex flex-col shadow-2xl"
            style={{ background: colors.sidebar }} data-testid="admin-sidebar-mobile">
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="lg:hidden flex items-center justify-between px-4 py-3 shrink-0"
          style={{ background: colors.sidebar, borderBottom: `1px solid ${colors.sidebarBorder}` }}
          data-testid="mobile-admin-header">
          <button onClick={() => setSidebarOpen(true)} className="p-2 -ml-2 rounded-xl"
            style={{ color: colors.text }} data-testid="open-sidebar-btn">
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4" style={{ color: colors.accent }} />
            <span className="font-bold text-sm" style={{ color: colors.text }}>Eili Admin</span>
          </div>
          <button onClick={toggleTheme} className="p-2 -mr-2 rounded-xl"
            style={{ color: colors.textMuted }} data-testid="mobile-theme-toggle">
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8" style={{ background: colors.mainBg }}>
          {children}
        </main>
      </div>
    </div>
  );
}
